import { useState } from "react";
import { toast } from "sonner";
import { Gift, Copy, Plus, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

const STYLE = { active: "text-emerald-300 bg-emerald-500/10", used: "text-cyan-300 bg-cyan-500/10", expired: "text-slate-500 bg-white/5" };
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

export function ReferralCodes({ referrals, slotsLeft, bonusDays, onChange }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try { await api.post("/account/referrals"); toast.success(t("Referral code created")); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create code"); }
    finally { setBusy(false); }
  };
  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success(t("Code copied")); };

  return (
    <div data-testid="referral-codes" className="glass rounded-2xl p-5">
      <div className="flex items-center justify-between mb-1">
        <div className="font-display font-semibold text-sm flex items-center gap-2"><Gift size={15} className="text-pink-400" /> {t("Referral codes")}</div>
        <button data-testid="referral-generate" onClick={generate} disabled={busy || slotsLeft === 0}
          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-600 to-cyan-600 text-white disabled:opacity-40">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} {t("New code")} ({slotsLeft} {t("left")})
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-4">{t("Share a code — your friend gets")} <span className="text-white">+{bonusDays} {t("days")}</span> {t("on their first pack and you get")} <span className="text-white">+{bonusDays} {t("days")}</span> {t("on yours. Up to 3 active codes, each valid 30 days.")}</p>
      {referrals.length === 0 && <div data-testid="referral-empty" className="text-xs text-slate-600 font-mono">{t("No codes yet.")}</div>}
      <div className="space-y-2">
        {referrals.map((r, i) => (
          <div key={r.code} data-testid={`referral-row-${i}`} className="flex items-center justify-between rounded-xl border border-white/10 px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-white">{r.code}</span>
              {r.status === "active" && <button data-testid={`referral-copy-${i}`} onClick={() => copy(r.code)} className="text-slate-500 hover:text-cyan-300"><Copy size={12} /></button>}
            </div>
            <div className="flex items-center gap-3 text-[10px] font-mono">
              <span className="text-slate-500">{r.status === "used" ? `${t("used by")} ${r.used_by_email}` : `${t("expires")} ${fmtDate(r.expires_at)}`}</span>
              <span className={`px-2 py-0.5 rounded ${STYLE[r.status]}`}>{t(r.status)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
